/**
 * The text layer of a PDF in the browser, for importing a supplier's invoice (R189; docs/specs/cloud/platform-costs.md
 * §4): pdf.js (Mozilla, Apache-2.0) reads the text of each page; nothing is drawn, no script or form in the PDF runs
 * and pdf.js never evaluates code it builds (`isEvalSupported` off). pdf.js loads only when an invoice is imported.
 */
import { pdfTextLines, type PdfTextPiece } from '@teslyn/domain';

/** The part of pdf.js used here (the browser build and the legacy build the tests use both have it). */
export interface PdfJs {
  readonly getDocument: (src: {
    data: Uint8Array;
    isEvalSupported: boolean;
    enableXfa: boolean;
    useSystemFonts: boolean;
    verbosity: number;
  }) => {
    readonly promise: Promise<{
      readonly numPages: number;
      getPage(n: number): Promise<{
        getTextContent(): Promise<{ readonly items: ReadonlyArray<object> }>;
      }>;
    }>;
    destroy(): Promise<void>;
  };
}

/** The pieces of text of every page, with their positions. */
export async function pdfTextPieces(lib: PdfJs, bytes: Uint8Array): Promise<PdfTextPiece[]> {
  // pdf.js takes the buffer over: hand it a copy so the caller keeps the bytes to upload
  const task = lib.getDocument({
    data: bytes.slice(),
    isEvalSupported: false,
    enableXfa: false,
    useSystemFonts: false,
    verbosity: 0,
  });
  const out: PdfTextPiece[] = [];
  try {
    const doc = await task.promise;
    for (let page = 1; page <= doc.numPages; page += 1) {
      const content = await (await doc.getPage(page)).getTextContent();
      for (const it of content.items) {
        const item = it as { str?: unknown; transform?: unknown };
        if (typeof item.str !== 'string' || !Array.isArray(item.transform)) continue;
        out.push({ page, x: Number(item.transform[4]), y: Number(item.transform[5]), str: item.str });
      }
    }
  } finally {
    await task.destroy();
  }
  return out;
}

let loaded: Promise<PdfJs> | null = null;

/** pdf.js for this browser, with its worker from Teslyn Cloud's own files (the CSP's worker-src 'self'). */
export function browserPdfJs(): Promise<PdfJs> {
  loaded ??= (async () => {
    const [lib, worker] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    lib.GlobalWorkerOptions.workerSrc = worker.default;
    return lib as unknown as PdfJs;
  })();
  return loaded;
}

/** The lines of a PDF's text layer (empty for a scan with no text), or the reason it could not be read. */
export async function pdfLines(
  bytes: Uint8Array,
  lib?: PdfJs,
): Promise<{ readonly lines: string[] } | { readonly error: string }> {
  try {
    return { lines: pdfTextLines(await pdfTextPieces(lib ?? (await browserPdfJs()), bytes)) };
  } catch (e) {
    const name = (e as { name?: string }).name ?? '';
    if (name === 'PasswordException')
      return { error: 'The PDF is protected by a password: Teslyn cannot read it.' };
    return { error: 'Teslyn could not read this PDF. Check it opens, or type the values.' };
  }
}
